import axios, { AxiosError, type AxiosInstance } from "axios"
import { useAuthStore } from "@/stores/auth"

export interface ApiErrorBody {
  success: false
  error: { code: string; message: string; details?: unknown }
}

export interface ApiSuccess<T> {
  success: true
  data: T
}

// S6 phase 5b (Option A): all authenticated HTTP goes SAME-ORIGIN via a proxy —
// the Vercel `/api/*` rewrite in production, the Vite dev-server proxy locally —
// so the httpOnly auth cookie is first-party (not a blocked third-party cookie).
// baseURL is therefore relative; VITE_API_URL is no longer read here (the socket
// client + the public token-in-URL clients still use it, since they stay direct
// to the backend). withCredentials makes the browser store + send the cookies.
export const api: AxiosInstance = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
})

// Read a cookie value by name from document.cookie. Used for the CSRF
// double-submit token — a non-httpOnly cookie the frontend echoes back in a
// header. Only works because the proxy makes csrf_token first-party (readable).
export function readCookie(name: string): string | null {
  const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1")
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"))
  return match ? decodeURIComponent(match[1]) : null
}

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"])

// Request interceptor (S6 5b/B1): auth is now 100% cookie-borne — the Bearer
// attach is gone (the JWT lives only in the httpOnly auth_token cookie, sent
// automatically via withCredentials). X-CSRF-Token echoes the csrf_token cookie
// on mutating requests — the backend CSRF guard now enforces this on every
// cookie-authenticated mutation (no Bearer to exempt it).
api.interceptors.request.use((config) => {
  if (MUTATING_METHODS.has((config.method ?? "").toLowerCase())) {
    const csrf = readCookie("csrf_token")
    if (csrf) config.headers["X-CSRF-Token"] = csrf
  }
  return config
})

// Response interceptor — on 401, clear LOCAL auth state only. Deliberately does
// NOT call the backend logout endpoint here: a 401 means the session is already
// invalid server-side (nothing to clear), and since /auth/logout is itself
// requireAuth-protected it would 401 too, re-triggering this very interceptor in
// a loop. User-initiated logout uses logout() below, which DOES hit the backend.
api.interceptors.response.use(
  (res) => res,
  (err: AxiosError<ApiErrorBody>) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().clear()
    }
    return Promise.reject(err)
  },
)

// User-initiated logout (S6 5b / A4): clear the httpOnly cookies server-side
// (JS can't clear httpOnly cookies), then clear local state — ALWAYS, even if
// the POST fails, so a network error never strands the user in a
// logged-in-looking state (worst case the cookie lingers until natural expiry).
// The POST inherits withCredentials + the X-CSRF-Token header from the request
// interceptor above.
export async function logout(): Promise<void> {
  try {
    await api.post("/auth/logout")
  } catch {
    // best-effort — fall through to the local clear regardless
  } finally {
    useAuthStore.getState().clear()
  }
}

export function extractErrorMessage(err: unknown, fallback = "Something went wrong."): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined
    if (body?.error?.message) return body.error.message
  }
  return fallback
}
